import { InvoicePricingObject } from '../types/invoices';
import { NewspaperOrderPricing } from '../types/newspaperOrder';
import {
  isFeeSplitTypeFullWaiver,
  TrackedFeeSplitResult
} from '../types/feeSplit';

/**
 * Closes out if the requested refund amount would leave a remaining balance of $0.50 or less,
 * to avoid rounding errors leaving invoices / transfer open. Also ensures we never return a
 * negative amount or over-refund.
 *
 * The $0.50 threshold was agreed on here:
 * https://columnpbc.slack.com/archives/C083848LSJK/p1772661479636669
 */
export const getAmountWithCloseoutVariance = ({
  totalAmount,
  alreadyProcessed,
  requestedAmount
}: {
  totalAmount: number;
  alreadyProcessed: number;
  requestedAmount: number;
}): number => {
  if (requestedAmount === 0) return 0;
  const allowedVarianceInCents = 50;
  const remaining = Math.max(0, totalAmount - alreadyProcessed);
  const cappedRequest = Math.min(requestedAmount, remaining);
  const proposedTotal = alreadyProcessed + cappedRequest;
  const shouldFullyClose =
    Math.abs(totalAmount - proposedTotal) <= allowedVarianceInCents;
  return shouldFullyClose ? remaining : cappedRequest;
};

export type RefundAmountBreakdown = {
  publisherRefundPortionInCents: number;
  taxRefundPortionInCents: number;
  convenienceFeeRefundPortionInCents: number;
  applicationFeeRefundPortionInCents: number;
};

/**
 * @returns ratable (proportional) breakdown of the refund amount across publisher,
 * convenience fee, tax, and application fee portions.
 *
 * totalInCents decomposes as publisherAmountInCents (tax-inclusive, net of the application
 * fee) + convenienceFeeInCents + applicationFeeInCents. The application fee portion is taken
 * as the remainder so the portions always sum back to refundAmountInCents; otherwise the
 * unattributed application fee strands cents in pendingRefundInCents on partial refunds.
 *
 * Floors the publisher portion so the least refund is assigned to the publisher.
 */
export const getRefundAmountBreakdownInCents = (params: {
  refundAmountInCents: number;
  pricing: NewspaperOrderPricing | InvoicePricingObject;
}): RefundAmountBreakdown => {
  const { refundAmountInCents, pricing } = params;
  const {
    totalInCents,
    taxesInCents,
    publisherAmountInCents,
    convenienceFeeInCents
  } = pricing;

  const publisherRefundPortionInCents = Math.floor(
    refundAmountInCents * (publisherAmountInCents / totalInCents)
  );
  const convenienceFeeRefundPortionInCents = Math.floor(
    refundAmountInCents * (convenienceFeeInCents / totalInCents)
  );
  const taxRefundPortionInCents = Math.floor(
    refundAmountInCents * (taxesInCents / totalInCents)
  );
  const applicationFeeRefundPortionInCents =
    refundAmountInCents -
    publisherRefundPortionInCents -
    convenienceFeeRefundPortionInCents -
    taxRefundPortionInCents;

  return {
    publisherRefundPortionInCents,
    taxRefundPortionInCents,
    convenienceFeeRefundPortionInCents,
    applicationFeeRefundPortionInCents
  };
};

/**
 * Computes the proportional fee-split credit (in cents) owed back to the publisher
 * when a notice or order is refunded.
 */
export const getFeeSplitRefundPortionInCents = ({
  trackedResult,
  refundAmountInCents,
  totalInCents
}: {
  trackedResult: TrackedFeeSplitResult | undefined;
  refundAmountInCents: number;
  totalInCents: number;
}): number => {
  if (
    !trackedResult ||
    trackedResult.amountInCents <= 0 ||
    isFeeSplitTypeFullWaiver(trackedResult.feeSplit)
  ) {
    return 0;
  }
  const ratio = totalInCents > 0 ? refundAmountInCents / totalInCents : 1;
  const credit = Math.floor(trackedResult.amountInCents * ratio);
  // Clamp the upper limit of the credit
  return Math.min(credit, trackedResult.amountInCents);
};
